import React from 'react';
import { UnscheduledEvent } from '../../types';
import { getErtsTimeDisplay } from './dateTimeUtils';
import { isNextScheduledFlightEarly } from './nextScheduledFlight';

/**
 * Props for {@link StdSection}.
 */
interface StdSectionProps {
  formData: UnscheduledEvent;
  /** The modal state; early-flight highlighting applies only while editing. */
  mode: 'view' | 'edit' | 'create';
  /** Marks APES values triggered by a Triage CREATE for user verification. */
  verifyFields?: boolean;
}

/**
 * Renders the read-only "Next Scheduled Flight" row: flight number, scheduled
 * departure date, and zulu time. These fields are system-managed — populated
 * from APES tail-mapping and kept current by the live subscription (open modal)
 * and the backend next-flight sync (homepage) — so they are not manually
 * editable. Date and time come from `nextScheduledFlightTimeZulu`; the flight
 * number from `nextScheduledFlightNumber`.
 */
const StdSection: React.FC<StdSectionProps> = ({ formData, mode, verifyFields }) => {
  const datePart = formData.nextScheduledFlightTimeZulu
    ? formData.nextScheduledFlightTimeZulu.split(' ')[0]
    : '';
  const flightNumberNeedsVerification: boolean = Boolean(
    verifyFields && formData.nextScheduledFlightNumber,
  );
  const scheduleNeedsVerification: boolean = Boolean(
    verifyFields && formData.nextScheduledFlightTimeZulu,
  );
  // Highlight only while editing; the homepage uses the same shared condition.
  const nextScheduledFlightIsEarly: boolean =
    mode !== 'view' && isNextScheduledFlightEarly(formData);
  const earlyFlightClass: string = nextScheduledFlightIsEarly
    ? ' field-next-scheduled-flight-early'
    : '';
  const nextFlightTitle: string | undefined = nextScheduledFlightIsEarly
    ? 'Next Scheduled Flight occurs before Next Advise or ERTS'
    : undefined;

  return (
    <div className="field-row">
      <label className="field-label">Next Scheduled Flight</label>
      <div className="field-group">
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Flight Number</span>
          <input
            type="text"
            value={formData.nextScheduledFlightNumber || ''}
            readOnly
            disabled
            placeholder="Auto-populated"
            className={`field-input${flightNumberNeedsVerification ? ' field-triage-verify' : ''}${earlyFlightClass}`}
            title={
              nextFlightTitle ||
              (flightNumberNeedsVerification
                ? 'Auto-populated after Triage; verify before saving'
                : undefined)
            }
          />
        </div>
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Date</span>
          <input
            type="date"
            value={datePart}
            readOnly
            disabled
            className={`field-input${scheduleNeedsVerification ? ' field-triage-verify' : ''}${earlyFlightClass}`}
            title={
              nextFlightTitle ||
              (scheduleNeedsVerification
                ? 'Auto-populated after Triage; verify before saving'
                : undefined)
            }
          />
        </div>
        <div className="field-input-block field-input-half">
          <span className="field-input-label">Zulu Time</span>
          <input
            type="text"
            value={getErtsTimeDisplay(formData.nextScheduledFlightTimeZulu)}
            readOnly
            disabled
            placeholder="HH:MM"
            className={`field-input${scheduleNeedsVerification ? ' field-triage-verify' : ''}${earlyFlightClass}`}
            title={
              nextFlightTitle ||
              (scheduleNeedsVerification
                ? 'Auto-populated after Triage; verify before saving'
                : undefined)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default StdSection;
